import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import PhoneInput from '../../components/base/PhoneInput';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import { AreaIcon, BathroomIcon, BedroomIcon, ParkingIcon } from '../../components/base/icons/MetricIcons';
import { Castle } from 'lucide-react';
import NearbyPlaces from '../../components/feature/NearbyPlaces';
import { PoiIcon } from '../../components/feature/poiIcons';
import PropertyRatings from '../../components/property/PropertyRatings';
import TravelTimeCard from '../../components/property/TravelTimeCard';
import SimilarProperties from '../../components/property/SimilarProperties';
import PropertyDisclaimer from '../../components/property/PropertyDisclaimer';
import { MarketPriceCard, MarketRentCard, ComparableDeals } from '../../components/market';
import { useMarketCard } from '../../hooks/useMarketCard';
import type { Property } from '../../mocks/properties';
import { trackContact, trackAreaView, trackShare } from '../../services/analytics';
import { formatNumber, formatDate, getCurrentLocale, isPriceOnRequest, priceOnRequestLabel, resolveImageUrl } from '../../utils/propertyUtils';
import { getPropertyTypeIconId } from '../../config/propertyTypeCatalog';
import JsonLd from '../../components/seo/JsonLd';
import Breadcrumbs from '../../components/base/Breadcrumbs';
import { useSeo } from '../../hooks/useSeo';
import {
  getPropertyDisplayAttributes,
  formatAttributeValue,
  type DisplayAttribute,
} from '../../utils/propertyAttributeUtils';
import { useAuth } from '../../hooks/useAuth';
import { translateAmenity, translateUtility } from '../../utils/amenityTranslations';
import { getAmenityIconId, getUtilityIconId } from '../../utils/amenityIcons';
import DomainIcon from '../../components/base/icons/domain/DomainIcon';
import { qrCodeSvg } from '../../utils/qrCode';
import { useFavorites } from '../../hooks/useFavorites';
import { useMessages } from '../../hooks/useMessages';
import { resolveApiBaseUrl } from '../../utils/apiClient';
import ListingBadges from '../../components/property/ListingBadges';
import { BrandRule } from '../../components/brand/BrandRule';
import { getPropertyDisplayTitle } from '../../utils/arabicPropertyTitle';
import { listingBadges } from '../../utils/listingBadges';

const API_BASE_URL = resolveApiBaseUrl();

interface MetricCard {
  key: string;
  icon: JSX.Element;
  value: string;
  label: string;
}

// One look for every section of the page (owner, 2026-10-07): white, split by a
// hairline, a teal heading (THEME.md §3), no box per section.
const SECTION = 'border-t border-[var(--pv-border-subtle)] pt-8';
const H2 = 'm-0 text-[1.25rem] font-semibold text-[var(--pv-color-primary)]';
const H3 = 'm-0 text-[1.0625rem] font-semibold text-[var(--pv-color-text)]';
const FEATURE_LIST = 'm-0 mt-4 grid list-none grid-cols-1 gap-x-6 gap-y-3 p-0 sm:grid-cols-2 lg:grid-cols-3';
const FEATURE_ITEM = 'flex items-center gap-2.5 text-[0.9375rem] text-[var(--pv-color-text)]';
const HEADER_ACTION =
  'inline-flex h-10 items-center gap-1.5 rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] px-4 text-[0.875rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)] touch-target';
// Save / share / report above the sticky contact card.
const STICKY_ACTION =
  'flex h-11 items-center justify-center gap-1.5 rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] text-[0.875rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)] hover:text-[var(--pv-color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30 disabled:cursor-wait disabled:opacity-60';
const CARD_TEXT_ACTION =
  'flex h-11 items-center justify-center gap-1.5 rounded-full text-[0.875rem] font-semibold text-[var(--pv-color-primary)] transition-colors hover:bg-[var(--pv-color-surface-sunken)] disabled:cursor-not-allowed disabled:opacity-50 touch-target';

const CONSTRUCTION_LABELS: Record<string, { ar: string; en: string }> = {
  READY: { ar: 'جاهز', en: 'Ready' },
  UNDER_CONSTRUCTION: { ar: 'تحت الإنشاء', en: 'Under Construction' },
  OFF_PLAN: { ar: 'على الخارطة', en: 'Off-plan' },
};
const STAGE_LABELS: Record<string, { ar: string; en: string }> = {
  FOUNDATION: { ar: 'الأساسات', en: 'Foundation' },
  STRUCTURE: { ar: 'الهيكل الإنشائي', en: 'Structure' },
  FINISHING: { ar: 'التشطيبات', en: 'Finishing' },
  FINAL_TOUCHES: { ar: 'اللمسات الأخيرة', en: 'Final Touches' },
};
const AD_CHANNEL_LABELS: Record<string, { ar: string; en: string }> = {
  licensed_platform: { ar: 'منصة مرخصة', en: 'Licensed Platform' },
  billboard: { ar: 'لوحة إعلانية', en: 'Billboard' },
  social_media: { ar: 'وسائل التواصل', en: 'Social Media' },
  newspaper: { ar: 'صحيفة', en: 'Newspaper' },
};

function formatRelativeDate(iso: string | undefined | null, locale: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (days < 1) return rtf.format(0, 'day');
    if (days < 30) return rtf.format(-days, 'day');
    if (days < 365) return rtf.format(-Math.floor(days / 30), 'month');
    return rtf.format(-Math.floor(days / 365), 'year');
  } catch {
    return '';
  }
}

export default function PropertyDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['property', 'search', 'common']);
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { createThread } = useMessages();

  const [property, setProperty] = useState<Property | null>(null);
  const [isCreatingEnquiry, setIsCreatingEnquiry] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Per-listing document title + meta description. Must live HERE (hooks
  // section, before the loading/not-found early returns) so the hook order is
  // identical on every render; falls back to nothing while loading.
  const seoIsArabic = i18n.language?.startsWith('ar');
  const seoTitle = property
    ? (seoIsArabic ? property.titleAr || property.title : property.title || property.titleAr)
    : undefined;
  const seoCity = property
    ? (seoIsArabic ? property.cityAr || property.city : property.city || property.cityAr)
    : undefined;
  const seoDesc = property
    ? (seoIsArabic ? property.descriptionAr || property.description : property.description || property.descriptionAr)
    : undefined;
  useSeo({
    title: seoTitle ? `${seoTitle}${seoCity ? ` - ${seoCity}` : ''} | ${seoIsArabic ? 'صفقات 24' : 'Safaqat 24'}` : undefined,
    description: seoDesc ? seoDesc.slice(0, 160) : undefined,
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  // Non-signed-in users must sign in to contact a broker (call/WhatsApp/email).
  // Clicking a contact action records the intent, then opens this prompt.
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [, setScheduleMessage] = useState<string | null>(null);
const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [contactFormSubmitting, setContactFormSubmitting] = useState(false);
  const [contactFormError, setContactFormError] = useState<string | null>(null);
  const [contactFormSuccess, setContactFormSuccess] = useState(false);

  // Modal focus management: remember the element that opened the lightbox so we
  // can restore focus on close, and hold refs to dialog containers + first
  // controls for focus-in on open and field-level validation focus.
  const lightboxTriggerRef = useRef<HTMLElement | null>(null);
  const lightboxRef = useRef<HTMLDivElement | null>(null);
  const signInPromptRef = useRef<HTMLDivElement | null>(null);
  const contactNameRef = useRef<HTMLInputElement | null>(null);
  const contactEmailRef = useRef<HTMLInputElement | null>(null);
  const contactMessageRef = useRef<HTMLTextAreaElement | null>(null);

  // Placeholder gradient for failed images
  const placeholderGradient = 'linear-gradient(135deg, #E1E9E8 0%, #CAD6D4 50%, #E1E9E8 100%)';

  useEffect(() => {
    async function fetchProperty() {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/properties/${id}`);

        if (!response.ok) {
          setProperty(null);
          return;
        }

        const responseJson = await response.json();
        // API returns { data: property } - extract the data field
        const data = responseJson.data || responseJson;

        // Transform API response to Property format
        // The API (propertyStore.toPropertyResponse) returns fields like:
        // propertyType, listingType, yearBuilt, dateAdded, lastUpdated, etc.
        const transformedProperty: Property = {
          id: String(data.id),
          title: data.title || '',
          titleAr: data.titleAr || data.title_ar || '',
          price: data.price || 0,
          bedrooms: data.bedrooms ?? data.rooms ?? 0,
          bathrooms: data.bathrooms || 0,
          sqft: data.sqft ?? (data.area_sqm ? Math.round(data.area_sqm * 10.7639) : 0),
          address: data.address ?? data.street_address ?? data.full_address ?? '',
          addressAr: data.addressAr || data.address_ar || '',
          city: data.city || '',
          cityAr: data.cityAr || data.city_ar || '',
          district: data.district || '',
          districtAr: data.districtAr || data.areaAr || '',
          state: data.state ?? data.region ?? '',
          stateAr: data.stateAr || data.state_ar || data.regionAr || '',
          zipCode: data.zipCode ?? data.postal_code ?? '',
          latitude: data.latitude || 0,
          longitude: data.longitude || 0,
          imageUrl: resolveImageUrl(data.imageUrl ?? data.image_url),
          galleryImages: (data.galleryImages ?? data.image_urls ?? []).map((url: string) => resolveImageUrl(url)),
          floorPlanImages: (data.floorPlanImages ?? []).map((url: string) => resolveImageUrl(url)),
          videoUrl: data.videoUrl ?? null,
          virtualTourUrl: data.virtualTourUrl ?? null,
          description: data.description || '',
          descriptionAr: data.descriptionAr || data.description_ar || '',
          // API returns propertyType (e.g., 'Apartment', 'Villa') not 'type'
          propertyType: data.propertyType || data.type || 'Apartment',
          // API returns listingType (e.g., 'RENT', 'SALE') not 'listing_type'
          listingType: data.listingType || data.listing_type || 'SALE',
          rentPeriod: data.rentPeriod || data.rent_period || 'yearly',
          // API returns yearBuilt (camelCase) not 'year_built'
          yearBuilt: data.yearBuilt || data.year_built || new Date().getFullYear(),
          parking: data.parking ?? data.hasParking ?? (data.parkingSpaces > 0 ? true : false),
          parkingSpaces: data.parkingSpaces || data.parking_spaces || 0,
          petFriendly: data.petFriendly ?? data.isPetFriendly ?? false,
          furnished: data.furnished ?? data.isFurnished ?? false,
          utilities: data.utilities || [],
          amenities: data.amenities || [],
          agentName: data.agentName || data.contact_name || '',
          agentNameAr: data.agentNameAr || data.agent_name_ar || '',
          agentPhone: data.agentPhone || data.contact_phone || '',
          agentEmail: data.agentEmail || data.contact_email || '',
          // API returns 'status' as 'available', 'pending', etc. (mapped from PropertyStatus)
          status: data.status || 'available',
          featured: data.featured ?? data.isFeatured ?? false,
          pricePerSqft: data.pricePerSqft || 0,
          // API returns dateAdded/lastUpdated (ISO strings)
          dateAdded: data.dateAdded || data.created_at,
          lastUpdated: data.lastUpdated || data.updated_at,
          // Engagement counters (drive the "الأكثر طلباً" badge)
          viewCount: data.viewCount ?? 0,
          inquiryCount: data.inquiryCount ?? 0,
          favoriteCount: data.favoriteCount ?? 0,
          // AI Data Engine fields
          pricePerSqm: data.pricePerSqm || (data.sqft > 0 ? Math.round((data.price || 0) / (data.sqft * 0.0929)) : 0),
          orientation: data.orientation,
          orientationAr: data.orientationAr,
          deedStatus: data.deedStatus,
          deedStatusAr: data.deedStatusAr,
          ownerType: data.ownerType,
          ownerTypeAr: data.ownerTypeAr,
          constructionStatus: data.constructionStatus,
          constructionStatusAr: data.constructionStatusAr,
          adLicenseNumber: data.adLicenseNumber,
          deedNumber: data.deedNumber,
          neighborhoodScore: data.neighborhoodScore,
          walkabilityScore: data.walkabilityScore,
          conditionRating: data.conditionRating,
          neighborhood: data.neighborhood,
          nearbyLandmarks: data.nearbyLandmarks,
          ownerId: data.ownerId || data.owner_id,
          // Apartment-specific fields
          apartmentNumber: data.apartmentNumber || data.apartment_number || data.unitNumber || data.unit_number,
          floorLevel: data.floorLevel || data.floor_level || data.floor,
          // Set when an admin published this listing from an
          // IndividualPropertyRequest. Drives the FAL-license notice.
          falLicensed: data.falLicensed === true,
          // REGA / details-page fields (see toPropertyResponse).
          propertyAge: data.propertyAge ?? null,
          residentialType: data.residentialType ?? null,
          verified: data.verified ?? false,
          verifiedAt: data.verifiedAt ?? null,
          streetWidth: data.streetWidth ?? null,
          planNumber: data.planNumber ?? null,
          landNumber: data.landNumber ?? null,
          warranty: data.warranty ?? null,
          notes: data.notes ?? null,
          falLicenseNumber: data.falLicenseNumber ?? null,
          licenseIssueDate: data.licenseIssueDate ?? null,
          licenseExpiryDate: data.licenseExpiryDate ?? null,
          adChannels: Array.isArray(data.adChannels) ? data.adChannels : [],
          encumbrance: data.encumbrance ?? null,
          saudiBuildingCodeCompliant: data.saudiBuildingCodeCompliant ?? null,
          mortgaged: data.mortgaged ?? null,
          restricted: data.restricted ?? null,
          boundaries: Array.isArray(data.boundaries) ? data.boundaries : [],
          constructionStage: data.constructionStage ?? null,
          constructionProgress: data.constructionProgress ?? null,
          constructionStartDate: data.constructionStartDate ?? null,
          expectedCompletionDate: data.expectedCompletionDate ?? null,
        };

        setProperty(transformedProperty);
        // Record a neighborhood view (feeds admin Neighborhood Intelligence).
        if (data.areaId) trackAreaView(data.areaId, { nameEn: data.neighborhood });
        setActiveImageIndex(0);
        setActiveGalleryTab('images');
        setActiveFpIndex(0);
        setShowContactForm(false);
        setScheduleMessage(null);
        setAreaExpanded(false);
        setMarketRequested(false);
      } catch (error) {
        console.error('Failed to fetch property:', error);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    }

    fetchProperty();
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  // On phones the price + contact bar is fixed along the bottom; while it shows,
  // lift the floating chat / feedback buttons above it (index.css, has-bottom-bar).
  const hasProperty = Boolean(property);
  useEffect(() => {
    if (!hasProperty) return;
    const root = document.documentElement;
    root.classList.add('has-bottom-bar');
    return () => root.classList.remove('has-bottom-bar');
  }, [hasProperty]);

  const propertyImages = useMemo(() => {
    if (!property) return [];

    // Collect all images, filtering out empty/undefined and removing duplicates
    const allImages: string[] = [];

    // Add main imageUrl first if it exists
    if (property.imageUrl && property.imageUrl.trim()) {
      allImages.push(property.imageUrl);
    }

    // Add gallery images, avoiding duplicates
    if (property.galleryImages?.length) {
      for (const img of property.galleryImages) {
        if (img && img.trim() && !allImages.includes(img)) {
          allImages.push(img);
        }
      }
    }

    // If we have real images, return them
    if (allImages.length > 0) {
      return allImages;
    }

    // No images available - return empty array, placeholder will be shown
    return [''];
  }, [property]);

  // Gallery category tabs. Only the ones the listing has are shown (owner,
  // 2026-10-07): greyed-out «فيديو» / «جولة افتراضية» tabs on every listing
  // wrapped onto a second row on phones and covered the photo.
  // Declared here (above early returns) to satisfy Rules of Hooks.
  const galleryTabs = useMemo(() => ([
    { key: 'images' as const,      available: propertyImages.length > 0 },
    { key: 'videos' as const,      available: !!property?.videoUrl },
    { key: 'floorPlan' as const,   available: (property?.floorPlanImages?.length ?? 0) > 0 },
    { key: 'virtualTour' as const, available: !!property?.virtualTourUrl },
  ]), [propertyImages.length, property?.videoUrl, property?.floorPlanImages?.length, property?.virtualTourUrl]);
  const [activeGalleryTab, setActiveGalleryTab] = useState<'images' | 'videos' | 'floorPlan' | 'virtualTour'>('images');

  // Lightbox: Esc closes, ←/→ navigate, and the page scroll is locked while open.
  // On open we remember the trigger and move focus into the dialog; on close we
  // restore focus to whatever opened it.
  useEffect(() => {
    if (!lightboxOpen) return;
    lightboxTriggerRef.current = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      else if (e.key === 'ArrowRight') setActiveImageIndex(prev => (prev + 1) % propertyImages.length);
      else if (e.key === 'ArrowLeft') setActiveImageIndex(prev => (prev - 1 + propertyImages.length) % propertyImages.length);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into the dialog so keyboard users land inside it.
    const focusTimer = window.setTimeout(() => lightboxRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
      // Restore focus to the element that opened the lightbox.
      lightboxTriggerRef.current?.focus?.();
      lightboxTriggerRef.current = null;
    };
  }, [lightboxOpen, propertyImages.length]);

  // Sign-in prompt modal: Esc closes, body scroll locked, focus the dialog on open.
  useEffect(() => {
    if (!showSignInPrompt) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowSignInPrompt(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => signInPromptRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
    };
  }, [showSignInPrompt]);

  // Contact/enquiry modal: Esc closes, body scroll locked, focus the first field on open.
  useEffect(() => {
    if (!showContactForm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowContactForm(false);
        setContactFormError(null);
        setContactFormSuccess(false);
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => contactNameRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
    };
  }, [showContactForm]);

  // Floor-plan image index (separate from activeImageIndex which is for photos)
  const [activeFpIndex, setActiveFpIndex] = useState(0);

  const locale = getCurrentLocale();
  const isRTL = i18n.dir() === 'rtl';
  const currencyName = t('common:currency.name');

  // Use language-appropriate fields based on current language
  const isArabicLang = i18n.language === 'ar';

  // Grouped category selector — overlaid at the bottom-left of whichever media
  // is showing. All four categories sit together next to the images chip;
  // unavailable ones render greyed-out + disabled. Defined once and dropped
  // into each tab's media panel so it stays put when you switch tabs.
  // (Declared after isRTL — it reads isRTL eagerly when the element is built.)
  const gallerySelector = (
    <div className="absolute bottom-4 left-4 z-10 flex flex-wrap items-center gap-1 rounded-[12px] bg-white/95 p-1 shadow-[0_2px_8px_rgba(0,77,64,0.12)]" dir={isRTL ? 'rtl' : 'ltr'}>
      {galleryTabs.filter((tab) => tab.available).map((tab) => {
        const isActive = activeGalleryTab === tab.key;
        const icon =
          tab.key === 'images' ? 'ri-camera-line'
          : tab.key === 'videos' ? 'ri-play-circle-line'
          : tab.key === 'floorPlan' ? 'ri-layout-3-line'
          : 'ri-compass-3-line';
        return (
          <button
            key={tab.key}
            type="button"
            disabled={!tab.available}
            onClick={() => tab.available && setActiveGalleryTab(tab.key)}
            className={`inline-flex items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-[13px] font-extrabold transition-all ${
              !tab.available
                ? 'cursor-not-allowed text-[#CBD7D4]'
                : isActive
                  ? 'bg-[#004D40] text-white shadow-sm'
                  : 'text-[var(--pv-color-text)] hover:bg-[var(--pv-color-surface-sunken)] hover:text-[#004D40]'
            }`}
            aria-pressed={isActive}
            aria-disabled={!tab.available}
          >
            <i className={`${icon} text-base`} aria-hidden></i>
            {tab.key === 'images'
              ? t('property.gallery.photoCount', { count: propertyImages.filter((img) => img).length || 0 })
              : t(`property.gallery.tabs.${tab.key}`)}
          </button>
        );
      })}
    </div>
  );

  // «عرض المزيد عن الحي» (owner, 2026-10-07): the district section shows its
  // headline scores; the rest (each service's score, the location record, the
  // registered sale prices and rents) opens on demand.
  const [areaExpanded, setAreaExpanded] = useState(false);
  // The market numbers are fetched the first time the section is opened, then kept.
  const [marketRequested, setMarketRequested] = useState(false);
  const handleAreaExpanded = useCallback((next: boolean) => {
    setAreaExpanded(next);
    if (next) setMarketRequested(true);
  }, []);

  // Registered-market context (MoJ sale deeds + REGA rent) for this listing's
  // district. Resolves to `available: false` whenever the district is too thin,
  // the type has no deed equivalent (villas are filed as "land"), or the
  // snapshot is not deployed (MARKET_DATA_FILE) — the page never depends on it.
  const { card: marketCard, loading: marketLoading } = useMarketCard(
    { kind: 'property', propertyId: property?.id },
    Boolean(property?.id) && marketRequested,
  );
  // Matched to a registry district — the card renders from here on, showing the
  // honest «بيانات غير كافية» state when the district is thin or the type has no
  // deed equivalent. Hiding it in those cases makes the feature look broken
  // rather than careful, and leaves a villa with no explanation of why it has no
  // price range.
  const marketMatched = Boolean(marketCard?.available);
  const marketHasSale = Boolean(marketCard?.available && marketCard.band);
  const marketHasRent = Boolean(marketCard?.available && marketCard.rent.length);

  const propertyIsFavorite = property ? isFavorite(property.id) : false;

  const handleFavoriteClick = useCallback(async () => {
    if (!property) return;

    if (!user) {
      navigate('/signin', { state: { from: { pathname: `/property/${property.id}` } } });
      return;
    }

    if (isTogglingFavorite) return;

    setIsTogglingFavorite(true);
    try {
      await toggleFavorite(property.id);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  }, [user, navigate, property, toggleFavorite, isTogglingFavorite]);

  const handleShare = useCallback(async () => {
    if (!property) return;

    const shareTitle = isArabicLang
      ? (property.titleAr || property.title)
      : (property.title || property.titleAr || '');
    const shareAddress = isArabicLang
      ? (property.addressAr || property.address)
      : (property.address || property.addressAr || '');
    const shareCity = isArabicLang
      ? (property.cityAr || property.city)
      : (property.city || property.cityAr || '');

    const shareData = {
      title: shareTitle,
      text: `${shareTitle} - ${shareAddress}, ${shareCity}`,
      url: window.location.href,
    };

    if (property?.id) trackShare('property', property.id, typeof navigator.share === 'function' ? 'native' : 'clipboard');

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled or share failed
        console.log('Share cancelled or failed:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        // Could show a toast notification here
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }
  }, [property, isArabicLang]);

  // IMPORTANT: All hooks must be before any early returns to follow React's Rules of Hooks
  const handleSendEnquiry = useCallback(async () => {
    if (!property) return;

    // If user is not logged in, record the intent and prompt sign-in (popup).
    if (!user) {
      if (property.id) trackContact('property', property.id, 'email');
      setShowSignInPrompt(true);
      return;
    }

    setIsCreatingEnquiry(true);
    try {
      const initialMessage = isArabicLang
        ? `مرحباً، أنا مهتم بهذا العقار: ${property.titleAr || property.title}`
        : `Hi, I'm interested in this property: ${property.title || property.titleAr}`;

      const thread = await createThread(property.id, initialMessage);

      if (thread) {
        // Track the enquiry
        trackContact('property', property.id, 'email');
        // Navigate to the enquiries page with the new thread
        navigate('/enquiries');
      } else {
        // If thread creation returned null, show the contact form as fallback
        setShowContactForm(true);
      }
    } catch (error) {
      console.error('Failed to create enquiry:', error);
      // Fallback to contact form on error
      setShowContactForm(true);
    } finally {
      setIsCreatingEnquiry(false);
    }
  }, [property, user, navigate, createThread, isArabicLang]);

  // Record a contact click and, for guests, intercept the link navigation to
  // prompt sign-in instead (call / WhatsApp). Signed-in users proceed normally.
  const handleContactLink = useCallback((e: React.MouseEvent, method: 'call' | 'whatsapp') => {
    if (property?.id) trackContact('property', property.id, method);
    if (!user) {
      e.preventDefault();
      setShowSignInPrompt(true);
    }
  }, [property?.id, user]);

  // Computed values that depend on property (safe to use before early returns)
  const parkingSpaces = property?.parkingSpaces ?? (property?.parking ? 1 : 0);
  const areaSqm = property ? Math.round(property.sqft / 10.7639) : 0;
  const formattedArea = formatNumber(areaSqm, locale);
  const onRequest = property ? isPriceOnRequest(property.price) : false;
  const formattedPrice = onRequest
    ? priceOnRequestLabel(isRTL)
    : (property ? formatNumber(property.price, locale) : '');

  // The listing marks (utils/listingBadges.ts): «مميز» and every mark it has earned.
  const hasListingBadges = property ? listingBadges(property).length > 0 : false;

  // Get dynamic attributes based on property type - MUST be before early returns
  const displayAttributes = useMemo(() => {
    if (!property) return [];
    const propertyData = {
      propertyType: property.propertyType,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      sqft: property.sqft,
      area: property.sqft,
      parking: parkingSpaces,
      yearBuilt: property.yearBuilt,
      floors: property.floors,
      furnished: property.furnished,
      petFriendly: property.petFriendly,
      orientation: property.orientation,
      floorLevel: property.floorLevel,
      apartmentNumber: property.apartmentNumber,
      livingRooms: property.livingRooms,
    };
    return getPropertyDisplayAttributes(propertyData, isArabicLang ? 'ar' : 'en');
  }, [property, parkingSpaces, isArabicLang]);

  // Helper to get icon component for an attribute - MUST be before early returns
  const getAttributeIcon = useCallback((attr: DisplayAttribute): JSX.Element => {
    switch (attr.key) {
      case 'bedrooms':
        return <BedroomIcon className="h-6 w-6" aria-hidden />;
      case 'bathrooms':
        return <BathroomIcon className="h-6 w-6" aria-hidden />;
      case 'area':
        return <AreaIcon className="h-6 w-6" aria-hidden />;
      case 'parking':
        return <ParkingIcon className="h-6 w-6" aria-hidden />;
      case 'floors':
        return <DomainIcon id="floors" size={24} className="shrink-0" />;
      default:
        return <i className={`${attr.icon} text-[1.5rem] leading-none`} aria-hidden />;
    }
  }, []);

  // Build metrics array from display attributes - MUST be before early returns
  const metrics: MetricCard[] = useMemo(() => {
    if (!property) return [];
    return displayAttributes
      // Skip boolean attributes that are false (no point showing "Not Furnished")
      .filter((attr) => {
        if (attr.key === 'furnished' || attr.key === 'petFriendly') {
          return attr.value === true;
        }
        return true;
      })
      // Also skip petFriendly/furnished for sale listings
      .filter((attr) => {
        if ((attr.key === 'furnished' || attr.key === 'petFriendly') && property.listingType?.toUpperCase() === 'SALE') {
          return false;
        }
        return true;
      })
      .map((attr) => {
        let formattedValue: string;

        if (attr.key === 'area') {
          formattedValue = formattedArea;
        } else if (attr.key === 'bedrooms' || attr.key === 'bathrooms') {
          formattedValue = formatNumber(attr.value as number, locale);
        } else if (attr.key === 'parking') {
          formattedValue = formatNumber(attr.value as number, locale);
        } else if (attr.key === 'yearBuilt') {
          formattedValue = String(attr.value);
        } else if (attr.key === 'floorLevel') {
          formattedValue = formatNumber(attr.value as number, locale);
        } else if (attr.key === 'unitNumber') {
          formattedValue = String(attr.value);
        } else if (attr.key === 'floors') {
          formattedValue = formatNumber(attr.value as number, locale);
        } else if (attr.key === 'orientation') {
          formattedValue = formatAttributeValue('orientation', attr.value, isArabicLang ? 'ar' : 'en');
        } else if (attr.key === 'furnished') {
          formattedValue = isArabicLang ? 'نعم' : 'Yes';
        } else if (attr.key === 'petFriendly') {
          formattedValue = isArabicLang ? 'نعم' : 'Yes';
        } else {
          formattedValue = formatAttributeValue(attr.key, attr.value, isArabicLang ? 'ar' : 'en');
        }

        return {
          key: attr.key,
          icon: getAttributeIcon(attr),
          value: formattedValue,
          label: attr.label,
        };
    });
  }, [property, displayAttributes, formattedArea, locale, isArabicLang, getAttributeIcon]);

  // Early return for loading state - AFTER all hooks
  if (loading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <main className="flex h-96 items-center justify-center px-6">
          <div className="text-center" role="status">
            <i className="ri-loader-4-line mb-4 text-5xl text-[#004D40] animate-spin" aria-hidden="true"></i>
            <p className="text-slate-600">{t('property.loading.title')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Early return for not found state - AFTER all hooks
  if (!property) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <main className="mx-auto flex max-w-4xl flex-col items-center px-6 py-16 text-center">
          <i className="ri-home-4-line mb-6 text-7xl text-slate-300"></i>
          <h1 className="text-3xl font-bold text-[var(--pv-color-primary)]">{t('property.empty.title')}</h1>
          <p className="mt-3 max-w-2xl text-slate-600">{t('property.empty.description')}</p>
          <Button className="mt-6" onClick={() => navigate('/search')}>
            <i className={`ri-search-line text-lg ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
            {t('property.empty.cta')}
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  // ===== Below this point, property is guaranteed to be defined =====

  // Use Arabic fields when language is Arabic, otherwise use default fields
  // The Arabic page shows an Arabic title: the listing's own, or one built from
  // its type, rooms and city when it only has an English one (as the cards do).
  const title = isArabicLang
    ? getPropertyDisplayTitle(property, true)
    : (property.title || property.titleAr || 'Property');
  const address = isArabicLang ? (property.addressAr || property.address) : (property.address || property.addressAr || '');
  const city = isArabicLang ? (property.cityAr || property.city) : (property.city || property.cityAr || '');
  const state = isArabicLang ? (property.stateAr || property.state) : (property.state || property.stateAr || '');
  const description = isArabicLang ? (property.descriptionAr || property.description) : (property.description || property.descriptionAr || '');
  const amenities = property.amenities;
  const utilities = property.utilities;

  // Per-listing structured data. Dynamic values (listing text) flow through
  // serializeJsonLd inside <JsonLd> — never inline them raw. (The useSeo hook
  // call lives up in the hooks section, before the early returns.)
  const listingSchema = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: title,
    description: description ? description.slice(0, 300) : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    ...(property.imageUrl ? { image: property.imageUrl } : {}),
    address: {
      '@type': 'PostalAddress',
      streetAddress: address || undefined,
      addressLocality: city || undefined,
      addressRegion: state || undefined,
      addressCountry: 'SA',
    },
    ...(property.price
      ? { offers: { '@type': 'Offer', price: property.price, priceCurrency: 'SAR' } }
      : {}),
  };

  const crumbs = [
    { label: isArabicLang ? 'الرئيسية' : 'Home', to: '/' },
    { label: isArabicLang ? 'بحث' : 'Search', to: '/search' },
    ...(city ? [{ label: city, to: `/search?city=${encodeURIComponent(property.city || city)}&listingType=all&category=all` }] : []),
    { label: title },
  ];

  // REGA advertising-licence verification link + locally-generated QR. The QR
  // encodes only our own constructed URL (no markup), so the inline SVG is safe.
  const licenseVerifyUrl = property.adLicenseNumber
    ? `https://safaqat24.sa/verify/ad/${encodeURIComponent(property.adLicenseNumber)}`
    : '';
  const licenseQrSvg = licenseVerifyUrl ? qrCodeSvg(licenseVerifyUrl, { ecc: 'MEDIUM' }) : '';
  const agentNameFallback = isArabicLang ? 'وسيط العقار' : 'Property Agent';
  const agentName = isArabicLang
    ? (property.agentNameAr || property.agentName || agentNameFallback)
    : (property.agentName || property.agentNameAr || agentNameFallback);
  const propertyExtras = property as unknown as Record<string, unknown>;
  const agentCompanyRaw = propertyExtras.agentCompany as string | null | undefined;
  const agentCompanyAr = propertyExtras.agentCompanyAr as string | null | undefined;
  const agentCompany = isArabicLang
    ? (agentCompanyAr || agentCompanyRaw || null)
    : (agentCompanyRaw || agentCompanyAr || null);
  const agentType = (propertyExtras.agentType as string) || 'broker';
  const agentTitle = agentCompany
    ? agentCompany
    : agentType === 'owner'
      ? (isArabicLang ? 'مالك العقار' : 'Property Owner')
      : t('property.contact.agentTitle');

  const typeIconId = getPropertyTypeIconId(property.propertyType);
  const statusLabel = t(`property.status.${property.status}`, { defaultValue: property.status });
  // A chip only for a real change of state (THEME.md §2.4c): in progress is
  // the soft cyan chip, sold / rented the red one. «متاح» is the default, no chip.
  const statusChip =
    property.status === 'pending'
      ? 'bg-[#E2F6FD] text-[#0B5E78]'
      : property.status === 'sold' || property.status === 'rented'
        ? 'bg-[#FDE8E7] text-[#C0322A]'
        : '';
  const propertyTypeLabel = t(`search:search.filters.propertyTypeOptions.${(property.propertyType || '').toUpperCase()}`, {
    defaultValue: property.propertyType,
  });

  const priceAccessible = t('property.header.priceAccessible', {
    value: formattedPrice,
    currency: currencyName,
  });

  // "Updated 2 days ago" for the contact card - a listing's age reads faster
  // as a distance than as a date.
  const lastUpdatedRelative = formatRelativeDate(property.lastUpdated, locale);

  const isRent = property.listingType?.toUpperCase() === 'RENT';
  const isYearlyRent = isRent && property.rentPeriod !== 'monthly';
  const approxMonthly = !onRequest && isYearlyRent && property.price > 0
    ? Math.round(property.price / 12)
    : null;

  const rentPeriodLabel = property.rentPeriod === 'monthly' ? t('common:units.month') : t('common:units.year');


  // «الشارع، حي …، المدينة» in the page's language, each part once (an address
  // often already ends with the city) and no empty parts. Owner, 2026-10-07: the
  // line showed an English address and no district. On the Arabic page a part
  // written only in Latin letters is left out rather than mixed in; the region
  // stays in the location table.
  const hasArabic = (text: string) => /[\u0600-\u06FF]/.test(text);
  const inPageLanguage = (ar?: string | null, en?: string | null) =>
    isArabicLang ? ar || (en && hasArabic(en) ? en : '') : en || ar || '';
  const districtName = inPageLanguage(property.districtAr, property.district).trim();
  const placeParts = [
    inPageLanguage(property.addressAr, property.address),
    districtName && isArabicLang && !districtName.startsWith('حي') ? `حي ${districtName}` : districtName,
    city,
  ].map((part) => (part || '').trim()).filter(Boolean);
  const placeLine = placeParts
    .filter((part, index) => !placeParts.slice(0, index).some((previous) => previous.includes(part)))
    .join(isArabicLang ? '، ' : ', ');

  // WhatsApp deep link with a prefilled inquiry message
  const whatsappNumber = property.agentPhone?.replace(/[^0-9]/g, '') || '';
  const whatsappMessage = encodeURIComponent(
    isArabicLang
      ? `مرحباً، أنا مهتم بهذا العقار: ${property.titleAr || property.title} - ${window.location.href}`
      : `Hi, I'm interested in this property: ${property.title || property.titleAr} - ${window.location.href}`
  );

  // Feature chips shown alongside amenities (design merges them into one row).
  // Parking is shown once, and distinguishes covered from plain: covered takes
  // precedence, so we never label uncovered parking as "covered".
  const featureChips: Array<{ key: string; icon: string; label: string }> = [];
  if (property.coveredParking) {
    featureChips.push({
      key: 'coveredParking',
      icon: 'parking',
      label: t('property.propertyFeatures.coveredParking', { count: property.parkingSpaces ?? 1 }),
    });
  } else if (property.parking) {
    featureChips.push({
      key: 'parking',
      icon: 'parking',
      label: t('property.propertyFeatures.parking', { count: property.parkingSpaces ?? 1 }),
    });
  }
  if (property.petFriendly && property.listingType?.toUpperCase() !== 'SALE') {
    featureChips.push({ key: 'petFriendly', icon: 'pet_friendly', label: t('property.propertyFeatures.petFriendly') });
  }
  if (property.furnished && property.listingType?.toUpperCase() !== 'SALE') {
    featureChips.push({ key: 'furnished', icon: 'furnished', label: t('property.propertyFeatures.furnished') });
  }

  // Amenities to display: drop parking entries that the feature chip already
  // covers (no duplicate "parking" tag), prefer covered over plain parking,
  // and de-duplicate repeated amenities.
  const normAmenity = (a: string) => a.toLowerCase().replace(/[_-]+/g, ' ').trim();
  const isCoveredParking = (a: string) => {
    const n = normAmenity(a);
    return n.includes('parking') && (n.includes('covered') || n.includes('مغطى') || n.includes('مظلل'));
  };
  const isPlainParking = (a: string) => {
    const n = normAmenity(a);
    return n === 'parking' || n === 'موقف سيارات' || n === 'موقف';
  };
  const parkingChipShown = featureChips.some((c) => c.key === 'coveredParking' || c.key === 'parking');
  const amenitiesHaveCovered = amenities.some(isCoveredParking);
  const seenAmenities = new Set<string>();
  const displayAmenities = amenities.filter((a) => {
    // Already represented by the parking feature chip → don't repeat it.
    if (parkingChipShown && (isPlainParking(a) || isCoveredParking(a))) return false;
    // If both covered and plain parking are listed, keep covered, drop plain.
    if (isPlainParking(a) && amenitiesHaveCovered) return false;
    const n = normAmenity(a);
    if (seenAmenities.has(n)) return false;
    seenAmenities.add(n);
    return true;
  });

  // The features as one icon list: the parking / pets / furnished flags first,
  // then the amenities (minus what a flag already says).
  const featureItems = [
    ...featureChips,
    ...displayAmenities.map((amenity) => ({
      key: amenity,
      icon: getAmenityIconId(amenity),
      label: translateAmenity(amenity, i18n.language),
    })),
  ];

  // Everything else the listing states, in one table. What the header, the key
  // facts or the contact card already show (place, status, listed date, year
  // built, reference no.) is not repeated; latitude / longitude are not shown.
  const ar = isArabicLang;
  const yesNo = (value?: boolean | null) =>
    value === null || value === undefined ? '' : value ? (ar ? 'نعم' : 'Yes') : (ar ? 'لا' : 'No');
  const metricKeys = new Set(metrics.map((metric) => metric.key));
  const constructionLabel = property.constructionStatus
    ? (CONSTRUCTION_LABELS[property.constructionStatus]?.[ar ? 'ar' : 'en'] ?? property.constructionStatus)
    : '';
  const detailRows = ([
    { label: t('property.info.labels.type'), value: propertyTypeLabel },
    {
      label: ar ? 'نوع السكن' : 'Residential type',
      value: property.residentialType
        ? property.residentialType === 'families' ? (ar ? 'عوائل' : 'Families') : (ar ? 'أفراد' : 'Singles')
        : '',
    },
    { label: ar ? 'حالة البناء' : 'Construction status', value: constructionLabel },
    { label: ar ? 'التأثيث' : 'Furnishing', value: property.furnished ? (ar ? 'مفروش' : 'Furnished') : (ar ? 'غير مفروش' : 'Unfurnished') },
    { label: t('property.info.labels.yearBuilt'), value: metricKeys.has('yearBuilt') ? '' : property.yearBuilt },
    {
      label: ar ? 'عمر العقار' : 'Property age',
      value: property.propertyAge != null ? (ar ? `${property.propertyAge} سنة` : `${property.propertyAge} yr`) : '',
    },
    {
      label: ar ? 'واجهة العقار' : 'Facade',
      value: property.orientation && !metricKeys.has('orientation')
        ? formatAttributeValue('orientation', property.orientation, ar ? 'ar' : 'en')
        : '',
    },
    {
      label: ar ? 'عرض الشارع' : 'Street width',
      value: property.streetWidth != null ? (ar ? `${property.streetWidth} م` : `${property.streetWidth} m`) : '',
    },
    { label: ar ? 'رقم المخطط' : 'Plan number', value: property.planNumber, ltr: true },
    { label: ar ? 'رقم صك الملكية' : 'Title deed number', value: property.deedNumber, ltr: true },
    { label: ar ? 'رقم الأرض' : 'Land number', value: property.landNumber, ltr: true },
    { label: ar ? 'الضمانات والمدة' : 'Warranty', value: property.warranty },
    { label: ar ? 'مطابق لكود البناء السعودي' : 'Saudi Building Code', value: yesNo(property.saudiBuildingCodeCompliant) },
    { label: ar ? 'يوجد التزام على العقار' : 'Has encumbrance', value: yesNo(property.encumbrance) },
    { label: ar ? 'العقار مرهون' : 'Mortgaged', value: yesNo(property.mortgaged) },
    { label: ar ? 'العقار مقيد' : 'Restricted', value: yesNo(property.restricted) },
    {
      label: ar ? 'قنوات الإعلان' : 'Advertising channels',
      value: property.adChannels?.length
        ? property.adChannels.map((channel) => AD_CHANNEL_LABELS[channel]?.[ar ? 'ar' : 'en'] ?? channel).join(ar ? '، ' : ', ')
        : '',
    },
    { label: ar ? 'تم التحقق في' : 'Verified on', value: property.verified && property.verifiedAt ? formatDate(property.verifiedAt, locale) : '' },
  ] as Array<{ label: string; value: string | number | null | undefined; ltr?: boolean }>).filter(
    (row) => row.value !== null && row.value !== undefined && row.value !== '',
  );

  // The full location record, shown inside «عرض المزيد عن الحي» (no exact
  // building number). The header shows the same place as one line.
  const locationRows = [
    { label: ar ? 'المنطقة' : 'Region', value: state },
    { label: ar ? 'المدينة' : 'City', value: city },
    { label: ar ? 'الحي' : 'District', value: ar ? property.districtAr || property.district : property.district },
    { label: ar ? 'اسم الشارع' : 'Street', value: address },
    { label: ar ? 'الرمز البريدي' : 'Postal code', value: property.zipCode, ltr: true },
    { label: ar ? 'خط العرض' : 'Latitude', value: property.latitude ? String(property.latitude) : '', ltr: true },
    { label: ar ? 'خط الطول' : 'Longitude', value: property.longitude ? String(property.longitude) : '', ltr: true },
  ].filter((row) => row.value);

  // Notable landmarks nearby (real, haversine to curated sites), shown inside
  // the location section above the travel times.
  const landmarkList = property.nearbyLandmarks && property.nearbyLandmarks.length > 0 ? (
    <div className="mt-6">
      <h3 className={H3}>{t('property.landmarks.title')}</h3>
      <ul role="list" className="m-0 mt-3 flex list-none flex-wrap gap-2 p-0">
        {property.nearbyLandmarks.map((lm) => {
          const name = i18n.language === 'ar' ? lm.nameAr : lm.nameEn;
          // Line icons, same set as the nearby-places list (no emoji).
          const icon = lm.type === 'religious'
            ? <PoiIcon category="WORSHIP" size={18} />
            : lm.type === 'heritage'
              ? <Castle size={18} strokeWidth={1.7} aria-hidden />
              : <i className="ri-map-pin-line text-[18px] leading-none" aria-hidden></i>;
          const great = lm.distanceKm <= 3 && (lm.type === 'religious' || lm.type === 'heritage');
          return (
            <li key={lm.key} className="inline-flex items-center gap-2 rounded-full border border-[var(--pv-border-soft)] px-3.5 py-2 text-[0.875rem] text-[var(--pv-color-text)]">
              <span className="text-[var(--pv-color-primary)]">{icon}</span>
              {t('property.landmarks.fromLabel', { distance: formatNumber(lm.distanceKm, locale), name })}
              {great && (
                <span className="ms-1 rounded-full bg-[var(--pv-color-surface-sunken)] px-2 py-0.5 text-[0.75rem] font-semibold text-[var(--pv-color-primary)]">
                  {t('property.landmarks.greatToVisit')}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  const handleScheduleViewing = () => {
    if (!user) {
      navigate('/signin', { state: { from: { pathname: `/property/${property.id}` } } });
      return;
    }
    setScheduleMessage(null);
    setShowContactForm(true);
  };

  const handleContactToggle = () => {
    setShowContactForm((previous) => !previous);
    // Reset form state when closing
    if (showContactForm) {
      setContactFormError(null);
      setContactFormSuccess(false);
    }
  };

  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (contactFormError) setContactFormError(null);
  };

  const handleContactFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactFormError(null);
    setContactFormSuccess(false);

    // Basic validation — on failure, focus the first invalid field.
    if (!contactForm.name.trim()) {
      setContactFormError(t('property.form.errors.nameRequired', { defaultValue: 'Please enter your name' }));
      contactNameRef.current?.focus();
      return;
    }
    if (!contactForm.email.trim()) {
      setContactFormError(t('property.form.errors.emailRequired', { defaultValue: 'Please enter your email' }));
      contactEmailRef.current?.focus();
      return;
    }
    if (!contactForm.message.trim()) {
      setContactFormError(t('property.form.errors.messageRequired', { defaultValue: 'Please enter a message' }));
      contactMessageRef.current?.focus();
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      setContactFormError(t('property.form.errors.invalidEmail', { defaultValue: 'Please enter a valid email address' }));
      contactEmailRef.current?.focus();
      return;
    }

    setContactFormSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/messages/property-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId: id,
          senderName: contactForm.name.trim(),
          senderEmail: contactForm.email.trim(),
          senderPhone: contactForm.phone.trim() || undefined,
          message: contactForm.message.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Track the contact
      trackContact('property', id || '', 'email');

      // Success
      setContactFormSuccess(true);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setContactFormError(t('property.form.errors.sendFailed', { defaultValue: 'Failed to send message. Please try again.' }));
    } finally {
      setContactFormSubmitting(false);
    }
  };

  // Side images in the gallery grid: the two images after the active one
  const sideImageIndexes = propertyImages.length > 1
    ? [1, 2].map((offset) => (activeImageIndex + offset) % propertyImages.length).filter((idx) => idx !== activeImageIndex)
    : [];

  // Helper: convert a YouTube / Vimeo URL to an embeddable URL, or null
  const toEmbedUrl = (url: string): string | null => {
    // YouTube — handles youtu.be/<id>, youtube.com/watch?v=<id>, /embed/<id>
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([A-Za-z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return null;
  };

  const renderImage = (
    index: number,
    heightClass: string,
    alt: string,
    opts?: { priority?: boolean },
  ) =>
    imageErrors.has(index) || !propertyImages[index] ? (
      <div
        className={`${heightClass} w-full flex items-center justify-center`}
        style={{ background: placeholderGradient }}
      >
        <div className="text-center text-slate-400">
          <i className="ri-building-2-line text-6xl opacity-60" aria-hidden="true"></i>
        </div>
      </div>
    ) : (
      <img
        src={propertyImages[index]}
        alt={alt}
        width={1280}
        height={853}
        decoding="async"
        {...(opts?.priority ? {} : { loading: 'lazy' as const })}
        className={`${heightClass} w-full object-cover`}
        onError={() => setImageErrors(prev => new Set(prev).add(index))}
      />
    );

  return (
    <div className="min-h-screen bg-white text-[var(--pv-color-text)]" dir={i18n.dir()}>
      <Navigation />

      {/* No bottom padding here: on phones the room for the fixed bar is added
          after the footer (index.css, has-bottom-bar). */}
      <main>
        <JsonLd data={listingSchema} />
        {/* ============ TITLE + PRICE ============
            One header: where you are, what it is, where it is and what it costs.
            Save and share live here only (owner, 2026-10-07: nothing twice). */}
        <section className="mx-auto max-w-7xl px-4 pt-5 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Breadcrumbs crumbs={crumbs} />
            <div className="flex items-center gap-2">
              {/* Owner action. Brokers edit their listing directly. A
                  FAL-managed individual listing (falLicensed — published under
                  Safaqat24's license) can't be self-edited, so its submitter
                  instead requests a change that lands with admin via the
                  complaints channel. The Edit button only shows to the owner,
                  and for FAL listings ownerId === the individual submitter. */}
              {user && property.ownerId && user.id === property.ownerId && (
                <button
                  type="button"
                  onClick={() => navigate(property.falLicensed ? `/complaints?propertyId=${property.id}` : `/property/${property.id}/edit`)}
                  className={HEADER_ACTION}
                >
                  <i className="ri-edit-line text-base leading-none" aria-hidden="true"></i>
                  {property.falLicensed ? (isRTL ? 'طلب تعديل' : 'Request a change') : (isRTL ? 'تعديل' : 'Edit')}
                </button>
              )}
              {/* Share and save sit here on phones; from lg they follow the reader
                  in the row above the contact card. */}
              <button type="button" onClick={handleShare} className={`${HEADER_ACTION} lg:hidden`}>
                <i className="ri-share-line text-base leading-none" aria-hidden="true"></i>
                {t('property.header.share')}
              </button>
              <button
                type="button"
                onClick={handleFavoriteClick}
                disabled={isTogglingFavorite}
                aria-pressed={propertyIsFavorite}
                className={`${HEADER_ACTION} ${propertyIsFavorite ? 'text-[#C0322A]' : ''} disabled:cursor-wait disabled:opacity-60 lg:hidden`}
              >
                <i className={`${propertyIsFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-base leading-none`} aria-hidden="true"></i>
                {t('property.header.save')}
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 flex-1">
              {/* Marks that change what the listing is worth knowing: featured
                  (gold), the marks it has earned (asked about, saved, viewed,
                  new), and published under our FAL licence. */}
              {(hasListingBadges || property.falLicensed) && (
                <div className="mb-3 flex flex-wrap items-center gap-2 text-[0.8125rem] font-semibold">
                  <ListingBadges listing={property} size="md" />
                  {/* FAL-licensed listings are published by Safaqat24 on behalf
                      of a private owner who isn't a registered broker. */}
                  {property.falLicensed && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--pv-border-strong)] px-3 py-1 text-[var(--pv-color-text)]"
                      title={isRTL
                        ? 'هذا العقار معروض من خلال رخصة فال الخاصة بنا نيابة عن المالك'
                        : 'This property is listed under our FAL license on behalf of the owner'}
                    >
                      <i className="ri-shield-check-line text-base leading-none text-[var(--pv-color-primary)]" aria-hidden="true"></i>
                      {isRTL ? 'تحت رخصة فال صفقات 24' : 'Listed under Safaqat24 FAL'}
                    </span>
                  )}
                </div>
              )}

              <h1 className="m-0 text-[1.625rem] font-bold leading-tight text-[var(--pv-color-primary)] sm:text-[2rem]">{title}</h1>
              {/* The logo's own colours, once, small (THEME.md §3.2 BrandRule). */}
              <BrandRule className="mt-3" />
              {placeLine && (
                <p className="m-0 mt-3 flex items-center gap-1.5 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                  <i className="ri-map-pin-line shrink-0 text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true"></i>
                  <span className="line-clamp-2 sm:line-clamp-none">{placeLine}</span>
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.875rem]">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--pv-border-soft)] px-3 py-1 font-medium text-[var(--pv-color-text)]">
                  <DomainIcon id={typeIconId} size={16} className="shrink-0 text-[var(--pv-color-primary)]" />
                  {propertyTypeLabel}
                </span>
                {/* Only a real change of state gets a chip: «متاح» is the default. */}
                {statusChip && <span className={`rounded-full px-3 py-1 font-semibold ${statusChip}`}>{statusLabel}</span>}
                {/* When it was listed and how often it was seen, each with its own
                    word and icon (they used to share one unclear line under the price). */}
                {property.dateAdded && (
                  <span className="inline-flex items-center gap-1.5 text-[var(--pv-color-text-muted)]">
                    <i className="ri-calendar-line text-[1rem] leading-none" aria-hidden="true"></i>
                    {t('property.header.listedOn', { date: formatDate(property.dateAdded, locale) })}
                  </span>
                )}
                {property.viewCount ? (
                  <span className="inline-flex items-center gap-1.5 text-[var(--pv-color-text-muted)]">
                    <i className="ri-eye-line text-[1rem] leading-none" aria-hidden="true"></i>
                    {t('property.header.views', { count: property.viewCount, formatted: formatNumber(property.viewCount, locale) })}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="shrink-0 lg:text-end">
              <p className="m-0 flex items-baseline gap-2 lg:justify-end" aria-label={priceAccessible}>
                {!onRequest && (
                  <SaudiRiyalSymbol className="h-[1.375rem] w-[1.375rem] shrink-0 self-center text-[var(--pv-color-primary)]" aria-hidden />
                )}
                <span className={`font-bold leading-none text-[var(--pv-color-primary)] tabular-nums ${onRequest ? 'text-[1.75rem]' : 'text-[2rem] sm:text-[2.25rem]'}`}>
                  {formattedPrice}
                </span>
                {!onRequest && isRent && (
                  <span className="text-[0.9375rem] text-[var(--pv-color-text-muted)]">/ {rentPeriodLabel}</span>
                )}
              </p>
              {/* A yearly rent, in months: «يعادل ⃁ 7,500 في الشهر». */}
              {approxMonthly != null && (
                <p className="m-0 mt-2 text-[0.875rem] text-[var(--pv-color-text-muted)]">
                  <span className="sr-only">{t('property.header.monthlyAccessible', { value: formatNumber(approxMonthly, locale) })}</span>
                  <span aria-hidden="true" className="inline-flex items-center gap-1 lg:justify-end">
                    <span>{t('property.header.monthlyLead')}</span>
                    <SaudiRiyalSymbol className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="font-semibold tabular-nums text-[var(--pv-color-text)]">{formatNumber(approxMonthly, locale)}</span>
                    <span>{t('property.header.monthlyTail')}</span>
                  </span>
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ============ GALLERY (2fr/1fr grid like the design) ============ */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-6">
          <div className="space-y-4">

            {/* The category selector (images/videos/floor-plan/virtual-tour) is
                overlaid at the bottom-left of each tab's media panel via
                `gallerySelector` — see below. No separate top tab bar. */}

            {/* ——— Images tab (existing viewer — unchanged) ——— */}
            {activeGalleryTab === 'images' && (
              <>
                <div className="relative grid grid-cols-1 gap-3 lg:h-[460px] lg:grid-cols-[2fr_1fr]">
                  {/* Main image */}
                  <div className="relative h-64 xs:h-80 sm:h-96 lg:h-[460px] overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-white shadow-[0_1px_2px_rgba(0,77,64,0.04)]">
                    <button
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      className="block h-full w-full cursor-zoom-in"
                      aria-label={isArabicLang ? 'عرض الصورة بالحجم الكامل' : 'View full size'}
                    >
                      {renderImage(activeImageIndex, 'h-full', isArabicLang ? `${title} - صورة ${activeImageIndex + 1}` : `${title} gallery image ${activeImageIndex + 1}`, { priority: true })}
                    </button>

                    {/* Expand to fullscreen */}
                    <button
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur transition hover:bg-black/75 hover:scale-105"
                      aria-label={isArabicLang ? 'فتح بملء الشاشة' : 'Open fullscreen'}
                    >
                      <i className="ri-fullscreen-line text-xl" aria-hidden="true"></i>
                    </button>

                    {/* Navigation Arrows.
                        In RTL the gallery reads right-to-left, so the visually-left
                        button advances (next) and the visually-right one rewinds
                        (previous) — matches standard RTL carousel UX. The icons +
                        positions stay the same; only the click handlers flip. */}
                    {propertyImages.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            isRTL
                              ? setActiveImageIndex(prev => prev < propertyImages.length - 1 ? prev + 1 : 0)
                              : setActiveImageIndex(prev => prev > 0 ? prev - 1 : propertyImages.length - 1)
                          }
                          className="absolute top-1/2 left-3 sm:left-4 -translate-y-1/2 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-lg backdrop-blur transition hover:bg-white hover:scale-105"
                          aria-label={isRTL
                            ? (isArabicLang ? 'الصورة التالية'  : 'Next image')
                            : (isArabicLang ? 'الصورة السابقة' : 'Previous image')}
                        >
                          <i className="ri-arrow-left-s-line text-2xl"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            isRTL
                              ? setActiveImageIndex(prev => prev > 0 ? prev - 1 : propertyImages.length - 1)
                              : setActiveImageIndex(prev => prev < propertyImages.length - 1 ? prev + 1 : 0)
                          }
                          className="absolute top-1/2 right-3 sm:right-4 -translate-y-1/2 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-lg backdrop-blur transition hover:bg-white hover:scale-105"
                          aria-label={isRTL
                            ? (isArabicLang ? 'الصورة السابقة' : 'Previous image')
                            : (isArabicLang ? 'الصورة التالية'  : 'Next image')}
                        >
                          <i className="ri-arrow-right-s-line text-2xl"></i>
                        </button>
                      </>
                    )}

                    {/* Image Counter Badge - Always LTR for numbers */}
                    {propertyImages.length > 1 && (
                      <div className="absolute bottom-4 right-4 rounded-full bg-black/70 px-3 py-1.5 text-sm font-medium text-white backdrop-blur">
                        <span dir="ltr" className="inline-block">{activeImageIndex + 1} / {propertyImages.length}</span>
                      </div>
                    )}

                    {/* Category selector (incl. photo count) — bottom-left overlay */}
                    {gallerySelector}
                  </div>

                  {/* Side images column (design: two stacked tiles) */}
                  {sideImageIndexes.length > 0 && (
                    <div className="hidden lg:grid h-[460px] grid-rows-2 gap-3">
                      {sideImageIndexes.slice(0, 2).map((imageIndex) => (
                        <button
                          key={`side-${imageIndex}`}
                          type="button"
                          onClick={() => setActiveImageIndex(imageIndex)}
                          className="overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-white transition hover:opacity-90"
                          aria-label={isArabicLang ? `عرض الصورة ${imageIndex + 1}` : `View image ${imageIndex + 1}`}
                        >
                          {renderImage(imageIndex, 'h-full', isArabicLang ? `${title} - صورة مصغّرة ${imageIndex + 1}` : `${title} thumbnail ${imageIndex + 1}`)}
                        </button>
                      ))}
                      {sideImageIndexes.length === 1 && (
                        <div className="rounded-2xl border border-[var(--pv-border-soft)]" style={{ background: placeholderGradient }} />
                      )}
                    </div>
                  )}
                </div>

                {/* Thumbnail Strip - Horizontal Scroll at Bottom (always LTR for consistent ordering) */}
                {propertyImages.length > 1 && (
                  <div className="relative" dir="ltr">
                    <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
                      {propertyImages.map((image, index) => (
                        <button
                          key={`thumb-${index}`}
                          type="button"
                          onClick={() => setActiveImageIndex(index)}
                          className={`relative h-16 w-24 xs:h-20 xs:w-28 sm:h-24 sm:w-36 flex-shrink-0 overflow-hidden rounded-lg sm:rounded-xl transition-all duration-200 ${
                            index === activeImageIndex
                              ? 'ring-2 ring-[#004D40] ring-offset-2 scale-105 shadow-lg'
                              : 'opacity-70 hover:opacity-100 hover:scale-102'
                          }`}
                        >
                          {imageErrors.has(index) || !image ? (
                            <div
                              className="h-full w-full flex items-center justify-center"
                              style={{ background: placeholderGradient }}
                            >
                              <i className="ri-building-2-line text-2xl text-slate-400 opacity-60" aria-hidden="true"></i>
                            </div>
                          ) : (
                            <img
                              src={image}
                              alt={isArabicLang ? `صورة مصغّرة ${index + 1}` : `Thumbnail ${index + 1}`}
                              width={144}
                              height={96}
                              loading="lazy"
                              decoding="async"
                              className="h-full w-full object-cover"
                              onError={() => setImageErrors(prev => new Set(prev).add(index))}
                            />
                          )}
                          {index === activeImageIndex && (
                            <div className="absolute inset-0 bg-[#004D40]/10"></div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Scroll Indicators */}
                    <div className={`absolute top-1/2 ${isRTL ? 'right-0' : 'left-0'} -translate-y-1/2 h-full w-8 bg-gradient-to-${isRTL ? 'l' : 'r'} from-[#F6FAF9] pointer-events-none hidden sm:block`}></div>
                    <div className={`absolute top-1/2 ${isRTL ? 'left-0' : 'right-0'} -translate-y-1/2 h-full w-8 bg-gradient-to-${isRTL ? 'r' : 'l'} from-[#F6FAF9] pointer-events-none hidden sm:block`}></div>
                  </div>
                )}
              </>
            )}

            {/* ——— Videos tab ——— */}
            {activeGalleryTab === 'videos' && property.videoUrl && (() => {
              const embedUrl = toEmbedUrl(property.videoUrl);
              const isDirectVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(property.videoUrl);
              return (
                <div className="relative overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-white shadow-[0_1px_2px_rgba(0,77,64,0.04)]">
                  {gallerySelector}
                  {embedUrl ? (
                    <div className="aspect-video w-full">
                      <iframe
                        src={embedUrl}
                        title={title}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : isDirectVideo ? (
                    <div className="aspect-video w-full">
                      <video
                        controls
                        src={property.videoUrl}
                        className="h-full w-full rounded-2xl object-contain bg-black"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-4 py-16 px-6 text-center">
                      <i className="ri-play-circle-line text-5xl text-[#004D40]" aria-hidden></i>
                      <p className="text-[15px] text-[#617672]">{t('property.gallery.videoUnavailable')}</p>
                      <a
                        href={property.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-[#004D40] px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-90"
                      >
                        <i className="ri-external-link-line" aria-hidden></i>
                        {t('property.gallery.tabs.videos')}
                      </a>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ——— Floor Plan tab ——— */}
            {activeGalleryTab === 'floorPlan' && property.floorPlanImages && property.floorPlanImages.length > 0 && (() => {
              const fpImages = property.floorPlanImages!;
              return (
                <div className="space-y-3">
                  <div className="relative overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-white shadow-[0_1px_2px_rgba(0,77,64,0.04)] h-64 xs:h-80 sm:h-96 lg:h-[460px] flex items-center justify-center">
                    {gallerySelector}
                    <img
                      src={fpImages[activeFpIndex]}
                      alt={isArabicLang ? `${title} - مخطط ${activeFpIndex + 1}` : `${title} floor plan ${activeFpIndex + 1}`}
                      width={1280}
                      height={853}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-contain p-2"
                    />
                    {fpImages.length > 1 && (
                      <>
                        {/* In RTL the visually-left arrow advances (next) and the
                            visually-right arrow rewinds (previous) — mirrors the main gallery. */}
                        <button
                          type="button"
                          onClick={() =>
                            isRTL
                              ? setActiveFpIndex(prev => prev < fpImages.length - 1 ? prev + 1 : 0)
                              : setActiveFpIndex(prev => prev > 0 ? prev - 1 : fpImages.length - 1)
                          }
                          className="absolute top-1/2 left-3 sm:left-4 -translate-y-1/2 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-lg backdrop-blur transition hover:bg-white hover:scale-105"
                          aria-label={isRTL
                            ? (isArabicLang ? 'المخطط التالي'  : 'Next floor plan')
                            : (isArabicLang ? 'المخطط السابق' : 'Previous floor plan')}
                        >
                          <i className="ri-arrow-left-s-line text-2xl"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            isRTL
                              ? setActiveFpIndex(prev => prev > 0 ? prev - 1 : fpImages.length - 1)
                              : setActiveFpIndex(prev => prev < fpImages.length - 1 ? prev + 1 : 0)
                          }
                          className="absolute top-1/2 right-3 sm:right-4 -translate-y-1/2 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-lg backdrop-blur transition hover:bg-white hover:scale-105"
                          aria-label={isRTL
                            ? (isArabicLang ? 'المخطط السابق' : 'Previous floor plan')
                            : (isArabicLang ? 'المخطط التالي'  : 'Next floor plan')}
                        >
                          <i className="ri-arrow-right-s-line text-2xl"></i>
                        </button>
                        <div className="absolute bottom-4 right-4 rounded-full bg-black/70 px-3 py-1.5 text-sm font-medium text-white backdrop-blur">
                          <span dir="ltr" className="inline-block">{activeFpIndex + 1} / {fpImages.length}</span>
                        </div>
                      </>
                    )}
                  </div>
                  {fpImages.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1" dir="ltr">
                      {fpImages.map((img, idx) => (
                        <button
                          key={`fp-thumb-${idx}`}
                          type="button"
                          onClick={() => setActiveFpIndex(idx)}
                          className={`relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg transition-all duration-200 ${
                            idx === activeFpIndex
                              ? 'ring-2 ring-[#004D40] ring-offset-2 scale-105 shadow-lg'
                              : 'opacity-70 hover:opacity-100'
                          }`}
                        >
                          <img src={img} alt={isArabicLang ? `مخطط ${idx + 1}` : `Floor plan ${idx + 1}`} width={96} height={64} loading="lazy" decoding="async" className="h-full w-full object-contain bg-white" />
                          {idx === activeFpIndex && <div className="absolute inset-0 bg-[#004D40]/10" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ——— Virtual Tour tab (stub) ——— */}
            {/* TODO: real virtual-tour viewer — scaffolded only */}
            {activeGalleryTab === 'virtualTour' && (
              <div className="relative flex flex-col items-center justify-center gap-5 rounded-2xl border border-[var(--pv-border-soft)] bg-white py-16 px-6 shadow-[0_1px_2px_rgba(0,77,64,0.04)] text-center">
                {gallerySelector}
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)]">
                  <i className="ri-compass-3-line text-4xl text-[#004D40]" aria-hidden></i>
                </div>
                <p className="text-[16px] font-bold text-[var(--pv-color-text)]">{t('property.gallery.tourComingSoon')}</p>
                {property.virtualTourUrl && (
                  <a
                    href={property.virtualTourUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl border border-[#004D40] px-5 py-2.5 text-sm font-bold text-[#004D40] transition hover:bg-[var(--pv-color-surface-sunken)]"
                  >
                    <i className="ri-external-link-line" aria-hidden></i>
                    {t('property.gallery.tabs.virtualTour')}
                  </a>
                )}
              </div>
            )}

          </div>
        </section>

        {/* ============ MAIN GRID ============
            One column of sections split by hairlines (THEME.md §3: the page is
            white, sections are separated by --pv-border-subtle) beside one
            sticky contact card. It used to be fourteen boxed cards, some of
            them repeating each other (owner, 2026-10-07). */}
        <section className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-10 px-4 pb-10 pt-8 sm:px-6 lg:grid-cols-[1fr_22rem]">
          <article className="flex min-w-0 flex-col gap-8">
            {/* The key facts in one row: what the type has (land has no bedrooms). */}
            {metrics.length > 0 && (
              <ul role="list" className="m-0 grid list-none grid-cols-2 gap-x-6 gap-y-5 p-0 sm:grid-cols-3 xl:grid-cols-5">
                {metrics.map((metric) => (
                  <li key={metric.key} className="flex items-center gap-3">
                    <span className="shrink-0 text-[var(--pv-color-primary)]">{metric.icon}</span>
                    <span className="min-w-0">
                      <b className="block truncate text-[1.125rem] font-semibold leading-tight text-[var(--pv-color-text)] tabular-nums">{metric.value}</b>
                      <span className="block text-[0.8125rem] text-[var(--pv-color-text-muted)]">{metric.label}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {description.trim() && (
              <section className={SECTION}>
                <h2 className={H2}>{t('property.sections.description')}</h2>
                <p className="m-0 mt-3 whitespace-pre-line break-words text-[0.9375rem] leading-[1.9] text-[var(--pv-color-text)]">{description}</p>
              </section>
            )}

            {(featureItems.length > 0 || utilities.length > 0) && (
              <section className={SECTION}>
                <h2 className={H2}>{t('property.sections.features')}</h2>
                {featureItems.length > 0 && (
                  <ul role="list" className={FEATURE_LIST}>
                    {featureItems.map((item) => (
                      <li key={item.key} className={FEATURE_ITEM}>
                        <DomainIcon id={item.icon} size={20} className="shrink-0 text-[var(--pv-color-primary)]" />
                        {item.label}
                      </li>
                    ))}
                  </ul>
                )}
                {utilities.length > 0 && (
                  <>
                    <h3 className={`${H3} mt-6`}>{t('property.utilities.title')}</h3>
                    <ul role="list" className={FEATURE_LIST}>
                      {utilities.map((utility) => (
                        <li key={utility} className={FEATURE_ITEM}>
                          <DomainIcon id={getUtilityIconId(utility)} size={20} className="shrink-0 text-[var(--pv-color-primary)]" />
                          {translateUtility(utility, i18n.language)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            )}

            {/* Everything else the listing states, in one table: what used to be
                «معلومات العقار» (twice), «تفاصيل إضافية» and «الموقع». */}
            {detailRows.length > 0 && (
              <section className={SECTION}>
                <h2 className={H2}>{t('property.sections.info')}</h2>
                <dl className="m-0 mt-3 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                  {detailRows.map((row) => (
                    <div key={row.label} className="flex items-center justify-between gap-4 border-b border-[var(--pv-border-subtle)] py-3">
                      <dt className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{row.label}</dt>
                      <dd className="m-0 text-end text-[0.9375rem] font-semibold text-[var(--pv-color-text)]" dir={row.ltr ? 'ltr' : undefined}>
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                {property.notes && (
                  <p className="m-0 mt-4 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text)]">
                    <span className="font-semibold">{isArabicLang ? 'ملاحظات: ' : 'Notes: '}</span>
                    {property.notes}
                  </p>
                )}
              </section>
            )}

            {/* Construction progress: only for a not-yet-ready property that
                states at least one progress detail. */}
            {(property.constructionStatus === 'UNDER_CONSTRUCTION' || property.constructionStatus === 'OFF_PLAN') &&
              (property.constructionStage || property.constructionProgress != null || property.constructionStartDate || property.expectedCompletionDate) && (
              <section className={SECTION}>
                <h2 className={H2}>{isArabicLang ? 'تقدّم الإنشاء' : 'Construction progress'}</h2>
                <p className="m-0 mt-3 flex flex-wrap items-center gap-2 text-[0.875rem]">
                  <span className="rounded-full bg-[#E2F6FD] px-3 py-1 font-semibold text-[#0B5E78]">{constructionLabel}</span>
                  {property.constructionStage && STAGE_LABELS[property.constructionStage] && (
                    <span className="rounded-full border border-[var(--pv-border-soft)] px-3 py-1 font-medium text-[var(--pv-color-text)]">
                      {STAGE_LABELS[property.constructionStage][isArabicLang ? 'ar' : 'en']}
                    </span>
                  )}
                </p>
                {property.constructionProgress != null && (
                  <div className="mt-4">
                    <div className="mb-1.5 flex items-center justify-between text-[0.875rem] font-semibold text-[var(--pv-color-text)]">
                      <span>{isArabicLang ? 'نسبة الإنجاز' : 'Completion'}</span>
                      <span dir="ltr">{property.constructionProgress}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--pv-color-surface-sunken)]">
                      <div className="h-full rounded-full bg-[var(--pv-color-primary)]" style={{ width: `${Math.max(0, Math.min(100, property.constructionProgress))}%` }}></div>
                    </div>
                  </div>
                )}
                {(property.constructionStartDate || property.expectedCompletionDate) && (
                  <dl className="m-0 mt-3 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                    {property.constructionStartDate && (
                      <div className="flex items-center justify-between gap-4 border-b border-[var(--pv-border-subtle)] py-3">
                        <dt className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{isArabicLang ? 'تاريخ بدء الإنشاء' : 'Started'}</dt>
                        <dd className="m-0 text-[0.9375rem] font-semibold text-[var(--pv-color-text)]">{formatDate(property.constructionStartDate, locale)}</dd>
                      </div>
                    )}
                    {property.expectedCompletionDate && (
                      <div className="flex items-center justify-between gap-4 border-b border-[var(--pv-border-subtle)] py-3">
                        <dt className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{isArabicLang ? 'الإنجاز المتوقع' : 'Expected completion'}</dt>
                        <dd className="m-0 text-[0.9375rem] font-semibold text-[var(--pv-color-text)]">{formatDate(property.expectedCompletionDate, locale)}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </section>
            )}

            {/* Ownership boundaries: display only. */}
            {property.boundaries && property.boundaries.length > 0 && (
              <section className={SECTION}>
                <h2 className={H2}>{isArabicLang ? 'حدود العقار / الملكية' : 'Property boundaries'}</h2>
                <dl className="m-0 mt-3 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                  {property.boundaries.map((b, i) => (
                    <div key={i} className="border-b border-[var(--pv-border-subtle)] py-3">
                      <div className="flex items-center justify-between gap-4">
                        <dt className="text-[0.9375rem] font-semibold text-[var(--pv-color-text)]">{b.name}</dt>
                        <dd className="m-0 text-[0.9375rem] text-[var(--pv-color-text)]" dir="ltr">{b.length}</dd>
                      </div>
                      {b.detail && <p className="m-0 mt-1 text-[0.875rem] text-[var(--pv-color-text-muted)]">{b.detail}</p>}
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* The REGA advertising licence, with its verification QR. */}
            {(property.falLicenseNumber || property.adLicenseNumber) && (
              <section className={SECTION}>
                <h2 className={`${H2} flex items-center gap-2`}>
                  {isArabicLang ? 'معلومات ترخيص الإعلان' : 'Advertisement licence'}
                  <i className="ri-verified-badge-fill text-[1.25rem] leading-none text-[#B58A0A]" aria-hidden="true"></i>
                </h2>
                <div className="mt-3 flex flex-wrap items-start gap-8">
                  <dl className="m-0 min-w-0 flex-1 basis-[20rem]">
                    {[
                      { label: isArabicLang ? 'رقم رخصة فال' : 'FAL licence number', value: property.falLicenseNumber, ltr: true },
                      { label: isArabicLang ? 'رقم ترخيص الإعلان' : 'Advertisement licence number', value: property.adLicenseNumber, ltr: true },
                      { label: isArabicLang ? 'تاريخ إصدار الترخيص' : 'Issue date', value: property.licenseIssueDate ? formatDate(property.licenseIssueDate, locale) : '' },
                      { label: isArabicLang ? 'تاريخ انتهاء الترخيص' : 'Expiry date', value: property.licenseExpiryDate ? formatDate(property.licenseExpiryDate, locale) : '' },
                    ].filter((r) => r.value).map((r) => (
                      <div key={r.label} className="flex items-center justify-between gap-4 border-b border-[var(--pv-border-subtle)] py-3">
                        <dt className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{r.label}</dt>
                        <dd className="m-0 text-end text-[0.9375rem] font-semibold text-[var(--pv-color-text)]" dir={r.ltr ? 'ltr' : undefined}>{r.value}</dd>
                      </div>
                    ))}
                  </dl>
                  {licenseQrSvg && (
                    <div className="flex shrink-0 flex-col items-center gap-2">
                      <div
                        className="rounded-xl border border-[var(--pv-border-soft)] bg-white p-2 [&>svg]:h-28 [&>svg]:w-28"
                        dangerouslySetInnerHTML={{ __html: licenseQrSvg }}
                      />
                      {licenseVerifyUrl && (
                        <a href={licenseVerifyUrl} target="_blank" rel="noreferrer" className="text-[0.8125rem] font-medium text-[var(--pv-color-primary)] underline-offset-4 hover:underline">
                          {isArabicLang ? 'التحقق من الترخيص' : 'Verify the licence'}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                {import.meta.env.VITE_DEMO_LISTINGS !== 'false' ? (
                  <p className="m-0 mt-4 flex items-start gap-2 text-[0.8125rem] font-medium text-[#8C6A0B]">
                    <i className="ri-information-line shrink-0 text-base leading-none" aria-hidden="true"></i>
                    {isArabicLang
                      ? 'بيانات توضيحية: هذا إعلان تجريبي، وأرقام التراخيص المعروضة أمثلة غير حقيقية لأغراض العرض فقط.'
                      : 'Sample data: this is an example listing - the licence numbers shown are illustrative and not real.'}
                  </p>
                ) : (
                  <p className="m-0 mt-4 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
                    {isArabicLang ? 'إعلان معتمد ومطابق لاشتراطات الهيئة العامة للعقار.' : 'Approved advertisement compliant with REGA requirements.'}
                  </p>
                )}
              </section>
            )}

            {/* Location: the map, the notable landmarks, the travel times and the
                nearest places, as one section (it used to be three). */}
            {property.latitude && property.longitude ? (
              <NearbyPlaces
                className={SECTION}
                latitude={property.latitude}
                longitude={property.longitude}
                propertyName={title}
                afterMap={
                  <>
                    {landmarkList}
                    <TravelTimeCard latitude={property.latitude} longitude={property.longitude} className="mb-2 mt-6" />
                  </>
                }
              />
            ) : landmarkList ? (
              <section className={SECTION}>{landmarkList}</section>
            ) : null}

            {/* The district: its headline scores, then «عرض المزيد عن الحي» opens
                each service's score, the location record and the market. */}
            <PropertyRatings
              property={property}
              id="property-ratings"
              className={SECTION}
              expanded={areaExpanded}
              onExpandedChange={handleAreaExpanded}
            >
              {locationRows.length > 0 && (
                <div>
                  <h3 className={H3}>{t('property.sections.location')}</h3>
                  <dl className="m-0 mt-3 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                    {locationRows.map((row) => (
                      <div key={row.label} className="flex items-center justify-between gap-4 border-b border-[var(--pv-border-subtle)] py-3">
                        <dt className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{row.label}</dt>
                        <dd className="m-0 text-end text-[0.9375rem] font-semibold text-[var(--pv-color-text)]" dir={row.ltr ? 'ltr' : undefined}>
                          {row.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Registered-market context. Shown for every listing we can place
                  in a registry district — with the range when the data supports
                  one, and the reason why not when it does not. Only a listing we
                  cannot locate at all (about 5%) drops it entirely. The rent block
                  covers types the deed data cannot price (villas, floors), which
                  REGA's rent indicators do publish. */}
              {(marketLoading || marketMatched) && <MarketPriceCard card={marketCard} loading={marketLoading} />}
              {marketHasSale && marketCard?.available && marketCard.comps.length > 0 && (
                <ComparableDeals comps={marketCard.comps} />
              )}
              {marketHasRent && <MarketRentCard card={marketCard} />}
            </PropertyRatings>

            <SimilarProperties property={property} className={SECTION} />
          </article>

          {/* ===== CONTACT CARD =====
              The one sticky element: price, who is selling and under which
              licence, and the ways to reach them. One solid fill (Call); the
              rest is border and type (THEME.md 3, rules 3 and 7).
              Phones (owner, 2026-10-07: the end of the page was cramped): the
              bar fixed at the bottom already holds the price, WhatsApp and
              Call, so below lg the card keeps only who is selling and the
              two text actions. */}
          <aside className="flex flex-col gap-3 lg:sticky lg:top-24">
            {/* Save, share and report ride above the card and follow the reader
                down the page with it (owner, 2026-10-07: "they should be added
                back"). From lg only: on phones save and share sit by the title,
                and report is in the closing safety note. */}
            <div className="hidden grid-cols-3 gap-2 lg:grid">
              <button
                type="button"
                onClick={handleFavoriteClick}
                disabled={isTogglingFavorite}
                aria-pressed={propertyIsFavorite}
                className={`${STICKY_ACTION} ${propertyIsFavorite ? 'border-[#C0322A]/40 text-[#C0322A] hover:border-[#C0322A] hover:text-[#C0322A]' : ''}`}
              >
                <i className={`${propertyIsFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-[1.125rem] leading-none`} aria-hidden="true"></i>
                {t('property.header.save')}
              </button>
              <button type="button" onClick={handleShare} className={STICKY_ACTION}>
                <i className="ri-share-line text-[1.125rem] leading-none" aria-hidden="true"></i>
                {t('property.header.share')}
              </button>
              <Link to={`/complaints?propertyId=${property.id}`} className={STICKY_ACTION}>
                <i className="ri-flag-line text-[1.125rem] leading-none" aria-hidden="true"></i>
                {isArabicLang ? 'إبلاغ' : 'Report'}
              </Link>
            </div>
            <div className="rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)]">
              <div className="hidden px-5 pb-4 pt-5 lg:block">
                <p className="m-0 flex flex-wrap items-baseline gap-x-1.5 gap-y-1" aria-label={priceAccessible}>
                  {!onRequest && (
                    <SaudiRiyalSymbol className="h-5 w-5 shrink-0 self-center text-[var(--pv-color-primary)]" aria-hidden />
                  )}
                  <span className={`font-bold leading-none text-[var(--pv-color-primary)] tabular-nums ${onRequest ? 'text-[1.375rem]' : 'text-[1.75rem]'}`}>
                    {formattedPrice}
                  </span>
                  {!onRequest && isRent && (
                    <span className="text-[0.9375rem] text-[var(--pv-color-text-muted)]">/ {rentPeriodLabel}</span>
                  )}
                </p>
              </div>

              {/* Who is selling, and under which licence */}
              <div className="border-[var(--pv-border-subtle)] px-5 py-4 lg:border-t">
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] text-[1.25rem] font-bold text-[var(--pv-color-primary)]"
                    aria-hidden="true"
                  >
                    {(agentName || 'A').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-[1.0625rem] font-semibold text-[var(--pv-color-text)]">{agentName}</p>
                    <p className="m-0 flex items-center gap-1.5 text-[0.875rem] text-[var(--pv-color-text-muted)]">
                      {property.falLicenseNumber && (
                        <i className="ri-verified-badge-fill shrink-0 text-[1.125rem] leading-none text-[#B58A0A]" aria-hidden="true"></i>
                      )}
                      <span className="truncate">{agentTitle}</span>
                    </p>
                  </div>
                </div>

                <dl className="m-0 mt-4 flex flex-col gap-2 text-[0.875rem]">
                  {property.falLicenseNumber && (
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-[var(--pv-color-text-muted)]">{isArabicLang ? 'رقم رخصة فال' : 'FAL licence no.'}</dt>
                      <dd className="m-0 font-semibold text-[var(--pv-color-text)] tabular-nums" dir="ltr">{property.falLicenseNumber}</dd>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-[var(--pv-color-text-muted)]">
                      {property.adLicenseNumber
                        ? (isArabicLang ? 'رقم ترخيص الإعلان' : 'Ad licence no.')
                        : (isArabicLang ? 'الرقم المرجعي' : 'Reference no.')}
                    </dt>
                    <dd className="m-0 font-semibold text-[var(--pv-color-text)] tabular-nums" dir="ltr">
                      {property.adLicenseNumber || property.id.slice(0, 8).toUpperCase()}
                    </dd>
                  </div>
                  {lastUpdatedRelative && (
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-[var(--pv-color-text-muted)]">{isArabicLang ? 'آخر تحديث' : 'Last updated'}</dt>
                      <dd className="m-0 text-[var(--pv-color-text)]">{lastUpdatedRelative}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="border-t border-[var(--pv-border-subtle)] px-5 pb-5 pt-4">
                {property.agentPhone && (
                  <div className="hidden grid-cols-2 gap-2.5 lg:grid">
                    <a
                      href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handleContactLink(e, 'whatsapp')}
                      className="flex h-12 items-center justify-center gap-2 rounded-full border border-[#25D366]/60 bg-[var(--pv-color-surface)] text-[0.9375rem] font-semibold text-[var(--pv-color-text)] transition-colors hover:border-[#25D366] touch-target"
                    >
                      <i className="ri-whatsapp-line text-[1.25rem] leading-none text-[#1DA851]" aria-hidden="true"></i>
                      {t('property.contact.whatsapp')}
                    </a>
                    <a
                      href={`tel:${property.agentPhone}`}
                      onClick={(e) => handleContactLink(e, 'call')}
                      className="flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--pv-color-primary)] text-[0.9375rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)] touch-target"
                    >
                      <i className="ri-phone-line text-[1.25rem] leading-none" aria-hidden="true"></i>
                      {t('property.contact.callNow')}
                    </a>
                  </div>
                )}
                <div className={`grid grid-cols-2 gap-2 ${property.agentPhone ? 'lg:mt-2' : ''}`}>
                  <button type="button" onClick={handleSendEnquiry} disabled={isCreatingEnquiry} className={CARD_TEXT_ACTION}>
                    {isCreatingEnquiry ? (
                      <span role="status" className="inline-flex">
                        <i className="ri-loader-4-line animate-spin text-[1.125rem] leading-none" aria-hidden="true"></i>
                        <span className="sr-only">{t('property.form.sending', { defaultValue: 'Sending…' })}</span>
                      </span>
                    ) : (
                      <i className="ri-mail-line text-[1.125rem] leading-none" aria-hidden="true"></i>
                    )}
                    {t('property.contact.requestDetails')}
                  </button>
                  <button type="button" onClick={handleScheduleViewing} disabled={property.status !== 'available'} className={CARD_TEXT_ACTION}>
                    {/* THEME.md: the «معاينة» mark is gold; on white it takes the
                        graphic shade #B58A0A. */}
                    <i className="ri-calendar-line text-[1.125rem] leading-none text-[#B58A0A]" aria-hidden="true"></i>
                    {t('property.contact.scheduleButton')}
                  </button>
                </div>
              </div>
            </div>

            {/* «الإبلاغ عن هذا الإعلان» lives once, in the safety note at the end
                of the page (PropertyDisclaimer). */}
          </aside>
        </section>
      </main>

      {/* Fullscreen image lightbox */}
      {lightboxOpen && activeGalleryTab === 'images' && propertyImages[activeImageIndex] && (
        <div
          ref={lightboxRef}
          tabIndex={-1}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-auto overscroll-contain bg-black/95 outline-none"
          dir={i18n.dir()}
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={isArabicLang ? 'معرض صور العقار' : 'Property image gallery'}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label={isArabicLang ? 'إغلاق' : 'Close'}
          >
            <i className="ri-close-line text-2xl" aria-hidden="true"></i>
          </button>

          <img
            src={propertyImages[activeImageIndex]}
            alt={`${title} ${activeImageIndex + 1}`}
            className="max-h-[88vh] max-w-[92vw] select-none object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {propertyImages.length > 1 && (
            <>
              {/* Visually-left advances in RTL (standard RTL carousel UX). */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImageIndex(prev => isRTL
                    ? (prev + 1) % propertyImages.length
                    : (prev - 1 + propertyImages.length) % propertyImages.length);
                }}
                className="absolute top-1/2 left-4 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                aria-label={isRTL ? (isArabicLang ? 'الصورة التالية' : 'Next image') : (isArabicLang ? 'الصورة السابقة' : 'Previous image')}
              >
                <i className="ri-arrow-left-s-line text-3xl"></i>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImageIndex(prev => isRTL
                    ? (prev - 1 + propertyImages.length) % propertyImages.length
                    : (prev + 1) % propertyImages.length);
                }}
                className="absolute top-1/2 right-4 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                aria-label={isRTL ? (isArabicLang ? 'الصورة السابقة' : 'Previous image') : (isArabicLang ? 'الصورة التالية' : 'Next image')}
              >
                <i className="ri-arrow-right-s-line text-3xl"></i>
              </button>

              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-white">
                <span dir="ltr">{activeImageIndex + 1} / {propertyImages.length}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sign-in required before contacting a broker (guests only) */}
      {showSignInPrompt && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          dir={i18n.dir()}
          onClick={() => setShowSignInPrompt(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t('property.contact.authPrompt.title')}
        >
          <div
            ref={signInPromptRef}
            tabIndex={-1}
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] text-[#004D40]">
              <i className="ri-lock-2-line text-3xl" aria-hidden="true"></i>
            </div>
            <h3 className="m-0 mb-2 text-lg font-black text-[var(--pv-color-text)]">{t('property.contact.authPrompt.title')}</h3>
            <p className="m-0 mb-5 text-sm text-[#617672]">{t('property.contact.authPrompt.message')}</p>
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => navigate('/signin', { state: { from: { pathname: `/property/${property.id}` } } })}
                className="w-full rounded-xl bg-[#1B7566] px-4 py-3 text-sm font-extrabold text-white transition hover:opacity-90"
              >
                {t('property.contact.authPrompt.signIn')}
              </button>
              <button
                type="button"
                onClick={() => setShowSignInPrompt(false)}
                className="w-full rounded-xl bg-[var(--pv-color-surface-sunken)] px-4 py-3 text-sm font-bold text-[#617672] transition hover:bg-[#E0E9E7]"
              >
                {t('property.contact.authPrompt.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phones: the price and the ways to reach the advertiser, always in reach.
          From lg the sticky contact card does this job, so the bar is hidden there. */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)] lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        dir={i18n.dir()}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2.5">
          <p className="m-0 flex min-w-0 items-baseline gap-1.5" aria-label={priceAccessible}>
            {!onRequest && <SaudiRiyalSymbol className="h-4 w-4 shrink-0 self-center text-[var(--pv-color-primary)]" aria-hidden />}
            <span className="truncate text-[1.125rem] font-bold text-[var(--pv-color-primary)] tabular-nums">{formattedPrice}</span>
            {!onRequest && isRent && (
              <span className="shrink-0 text-[0.8125rem] text-[var(--pv-color-text-muted)]">/ {rentPeriodLabel}</span>
            )}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            {property.agentPhone ? (
              <>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => handleContactLink(e, 'whatsapp')}
                  aria-label={t('property.contact.whatsapp')}
                  className="grid h-11 w-11 place-items-center rounded-full border border-[#25D366]/60 text-[#1DA851] transition-colors hover:border-[#25D366]"
                >
                  <i className="ri-whatsapp-line text-[1.25rem] leading-none" aria-hidden="true"></i>
                </a>
                <a
                  href={`tel:${property.agentPhone}`}
                  onClick={(e) => handleContactLink(e, 'call')}
                  className="inline-flex h-11 items-center gap-1.5 rounded-full bg-[var(--pv-color-primary)] px-4 text-[0.875rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)]"
                >
                  <i className="ri-phone-line text-[1.125rem] leading-none" aria-hidden="true"></i>
                  {t('property.contact.callNow')}
                </a>
              </>
            ) : (
              <button
                type="button"
                onClick={handleSendEnquiry}
                disabled={isCreatingEnquiry}
                className="inline-flex h-11 items-center gap-1.5 rounded-full bg-[var(--pv-color-primary)] px-4 text-[0.875rem] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 dark:text-[var(--pv-color-surface)]"
              >
                <i className="ri-mail-line text-[1.125rem] leading-none" aria-hidden="true"></i>
                {t('property.contact.requestDetails')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Contact Form Modal Overlay */}
      {showContactForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-auto overscroll-contain bg-black/40 backdrop-blur-sm p-4"
          onClick={handleContactToggle}
          role="dialog"
          aria-modal="true"
          aria-label={t('property.contact.contactButton')}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" dir={i18n.dir()} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-slate-900">{t('property.contact.contactButton')}</h3>
              <button type="button" onClick={handleContactToggle} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition" aria-label={isArabicLang ? 'إغلاق' : 'Close'}>
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              {t('property.form.replyTimeNote', { defaultValue: 'We will be in touch within 1 to 5 working days.' })}
            </p>
            <form className="space-y-3" onSubmit={handleContactFormSubmit}>
              {contactFormSuccess && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700" role="status" aria-live="polite">
                  <i className="ri-checkbox-circle-fill mr-2" aria-hidden="true"></i>
                  {t('property.form.successMessage', { defaultValue: 'Your message has been sent. We will be in touch within 1 to 5 working days.' })}
                </div>
              )}
              {contactFormError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" aria-live="assertive">
                  <i className="ri-error-warning-fill mr-2" aria-hidden="true"></i>
                  {contactFormError}
                </div>
              )}
              <label htmlFor="contact-name" className="sr-only">{t('property.form.namePlaceholder')}</label>
              <input ref={contactNameRef} id="contact-name" type="text" name="name" autoComplete="name" value={contactForm.name} onChange={handleContactFormChange} placeholder={t('property.form.namePlaceholder')} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#004D40] focus:outline-none focus:ring-2 focus:ring-[#F2F8F7]" disabled={contactFormSubmitting} />
              <label htmlFor="contact-email" className="sr-only">{t('property.form.emailPlaceholder')}</label>
              <input ref={contactEmailRef} id="contact-email" type="email" name="email" autoComplete="email" inputMode="email" value={contactForm.email} onChange={handleContactFormChange} placeholder={t('property.form.emailPlaceholder')} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#004D40] focus:outline-none focus:ring-2 focus:ring-[#F2F8F7]" disabled={contactFormSubmitting} />
              <label htmlFor="contact-phone" className="sr-only">{t('property.form.phonePlaceholder')}</label>
              <PhoneInput
                id="contact-phone"
                name="phone"
                value={contactForm.phone}
                onChange={(value) => setContactForm((prev) => ({ ...prev, phone: value }))}
                placeholder={t('property.form.phonePlaceholder')}
                disabled={contactFormSubmitting}
              />
              <label htmlFor="contact-message" className="sr-only">{t('property.form.messagePlaceholder')}</label>
              <textarea ref={contactMessageRef} id="contact-message" rows={3} name="message" value={contactForm.message} onChange={handleContactFormChange} placeholder={t('property.form.messagePlaceholder')} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#004D40] focus:outline-none focus:ring-2 focus:ring-[#F2F8F7]" disabled={contactFormSubmitting} />
              <Button type="submit" className="w-full touch-target" disabled={contactFormSubmitting}>
                {contactFormSubmitting ? (
                  <span role="status" className="inline-flex items-center">
                    <i className="ri-loader-4-line animate-spin mr-2" aria-hidden="true"></i>
                    {t('property.form.sending', { defaultValue: 'Sending…' })}
                  </span>
                ) : (
                  t('property.form.submit')
                )}
              </Button>
            </form>
          </div>
        </div>
      )}

      <PropertyDisclaimer property={property} />

      <Footer />
    </div>
  );
}
